import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type {
  BankAccountDto,
  ImportStatementResponse,
  ReconciliationDto,
  ReconciliationWorkspace,
  StatementMatchesResponse,
} from '@hesabat/contracts';
import { Money, summarizeReconciliation } from '@hesabat/finance-core';
import { api, ProblemError, UnauthorizedError } from './api';
import { formatMoney, parseAmount } from './money';
import StatementImport from './StatementImport';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  canReconcile: boolean;
  initialBankAccountId: string | null;
  onSessionLost: () => void;
}

const ISSUE_LABELS: Record<string, string> = {
  RECONCILIATION_DATE_NOT_AFTER: 'تاريخ الكشف يجب أن يكون بعد آخر مطابقة',
  RECONCILIATION_LINE_DUPLICATE: 'سطر مختار مرتين',
  RECONCILIATION_LINE_UNKNOWN: 'سطر طوبق من قبل أو ليس على هذا الحساب — حدّث الشاشة',
  RECONCILIATION_LINE_AFTER_STATEMENT: 'سطر بتاريخ بعد الكشف',
  RECONCILIATION_OUT_OF_BALANCE: 'رصيد الكشف لا يساوى الرصيد المُطابق',
  BANK_ACCOUNT_INACTIVE: 'الحساب البنكى موقوف',
  STATEMENT_LINE_UNKNOWN: 'سطر كشف استُخدم من قبل أو بتاريخ بعد الكشف — حدّث الشاشة',
};

const REASON_LABELS: Record<string, string> = {
  SAME_DATE: 'نفس اليوم',
  NEAR_DATE: 'تاريخ قريب',
  DOCUMENT_NUMBER: 'رقم المستند فى البيان',
};

function today(): string {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

/**
 * المطابقة البنكية: يُدخل رصيد كشف البنك فى تاريخه، ويُعلَّم من سطور الدفتر ما ظهر فى
 * الكشف. تكتمل حين يساوى الكشف الرصيد السابق + صافى المُعلَّم — والفرق يُحسب هنا
 * بنفس دالة المحرك التى يتحقق بها الخادم. ما لم يظهر يبقى معلقًا للكشف التالى، ومصاريف
 * البنك وفوائده تُسجَّل مستندًا أولاً (مصروف نقدى) ثم تُطابق.
 */
export default function BankReconciliation({
  companyId,
  currency,
  canReconcile,
  initialBankAccountId,
  onSessionLost,
}: Props) {
  const [banks, setBanks] = useState<BankAccountDto[] | null>(null);
  const [bankAccountId, setBankAccountId] = useState(initialBankAccountId ?? '');
  const [statementDate, setStatementDate] = useState(today);
  const [statementBalance, setStatementBalance] = useState('');
  const [workspace, setWorkspace] = useState<ReconciliationWorkspace | null>(null);
  const [cleared, setCleared] = useState<Set<string>>(() => new Set());
  const [history, setHistory] = useState<ReconciliationDto[]>([]);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [completed, setCompleted] = useState<ReconciliationDto | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [matches, setMatches] = useState<StatementMatchesResponse | null>(null);
  // سطر الكشف ← سطر الدفتر المقبول له؛ يُرسل مع الإتمام فتُربط سطور الكشف وتثبت
  const [accepted, setAccepted] = useState<Map<string, string>>(() => new Map());
  const [lastImport, setLastImport] = useState<ImportStatementResponse | null>(null);

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        const issues = caught.problem.issues ?? [];
        setFailure(
          issues.length > 0
            ? issues.map((issue) => ISSUE_LABELS[issue.code] ?? issue.message).join('، ')
            : (caught.problem.detail ?? caught.problem.title),
        );
        return;
      }
      setFailure(caught instanceof Error ? caught.message : String(caught));
    },
    [onSessionLost],
  );

  useEffect(() => {
    api
      .treasuryAccounts(companyId)
      .then((data) => {
        const active = data.bankAccounts.filter((bank) => bank.isActive);
        setBanks(active);
        setBankAccountId((current) => current || active[0]?.id || '');
      })
      .catch(handleError);
  }, [companyId, handleError]);

  const load = useCallback(() => {
    if (!bankAccountId || !/^\d{4}-\d{2}-\d{2}$/.test(statementDate)) return;
    setFailure(null);
    api
      .reconciliationWorkspace(companyId, bankAccountId, statementDate)
      .then((loaded) => {
        setWorkspace(loaded);
        setCleared(new Set());
        setAccepted(new Map());
      })
      .catch(handleError);
    api
      .statementMatches(companyId, bankAccountId, statementDate)
      .then((loaded) => {
        setMatches(loaded);
        // رصيد الكشف من الملف اقتراح يملأ الحقل الفارغ فقط — لا يكتب فوق ما أدخله المحاسب
        if (loaded.closingBalance !== null) {
          // أصفار الكسر الزائدة فقط — «1200.0000» يُعرض 1200 لا 12
          const fromFile = Money.of(loaded.closingBalance)
            .toFixed(4)
            .replace(/(\.\d*?)0+$/, '$1')
            .replace(/\.$/, '');
          setStatementBalance((current) => (current.trim() ? current : fromFile));
        }
      })
      .catch(handleError);
    api.reconciliations(companyId, bankAccountId).then(setHistory).catch(handleError);
  }, [companyId, bankAccountId, statementDate, handleError]);

  useEffect(load, [load]);

  // رصيد الكشف قد يكون سالبًا (حساب مكشوف)، والحقل الفارغ «لم يُدخل» لا صفر
  const parsedBalance = useMemo(() => {
    const text = statementBalance.trim();
    if (!text) return null;
    const negative = text.startsWith('-');
    const value = parseAmount(negative ? text.slice(1) : text);
    if (value === null) return null;
    return negative && !Money.of(value).isZero() ? '-' + value : value;
  }, [statementBalance]);
  const summary = useMemo(() => {
    if (!workspace) return null;
    // الرصيد المُطابق يُعرض أثناء التعليم حتى قبل إدخال رصيد الكشف؛ الفرق وحده يحتاجه
    return summarizeReconciliation({
      previousBalance: workspace.previousBalance,
      statementBalance: parsedBalance ?? '0',
      available: workspace.lines.map((line) => ({
        id: line.journalLineId,
        postingDate: line.postingDate,
        debit: line.debit,
        credit: line.credit,
      })),
      cleared: [...cleared],
    });
  }, [workspace, parsedBalance, cleared]);

  // المعلق بعد المطابقة يفسّر فرق الدفتر عن البنك: إيداعات لم تظهر بعد وشيكات لم تُصرف
  const outstanding = useMemo(() => {
    const rest = (workspace?.lines ?? []).filter((line) => !cleared.has(line.journalLineId));
    return {
      deposits: Money.sum(rest.map((line) => line.debit)),
      withdrawals: Money.sum(rest.map((line) => line.credit)),
    };
  }, [workspace, cleared]);

  const dateTooEarly =
    workspace?.previousDate !== null &&
    workspace?.previousDate !== undefined &&
    statementDate <= workspace.previousDate;
  const ready =
    canReconcile &&
    parsedBalance !== null &&
    !!summary &&
    summary.difference.isZero() &&
    !dateTooEarly &&
    !submitting;

  function touched(): void {
    setIdempotencyKey(crypto.randomUUID());
    setFailure(null);
    setCompleted(null);
  }

  function toggle(id: string): void {
    const removing = cleared.has(id);
    setCleared((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (removing) {
      setAccepted(
        (current) => new Map([...current].filter(([, journalLineId]) => journalLineId !== id)),
      );
    }
    touched();
  }

  /** قبول مطابقة: يُعلَّم سطر الدفتر كما لو ضغطه المحاسب، ويُحفظ سطر الكشف المقابل له. */
  function accept(pairs: readonly (readonly [string, string])[]): void {
    if (pairs.length === 0) return;
    setCleared(
      (current) => new Set([...current, ...pairs.map(([, journalLineId]) => journalLineId)]),
    );
    setAccepted((current) => {
      const next = new Map(current);
      const taken = new Set(next.values());
      for (const [statementLineId, journalLineId] of pairs) {
        if (taken.has(journalLineId)) continue;
        next.set(statementLineId, journalLineId);
        taken.add(journalLineId);
      }
      return next;
    });
    touched();
  }

  function unaccept(statementLineId: string): void {
    const journalLineId = accepted.get(statementLineId);
    setAccepted((current) => {
      const next = new Map(current);
      next.delete(statementLineId);
      return next;
    });
    if (journalLineId) {
      setCleared((current) => {
        const next = new Set(current);
        next.delete(journalLineId);
        return next;
      });
    }
    touched();
  }

  function imported(result: ImportStatementResponse): void {
    setLastImport(result);
    setShowImport(false);
    load();
  }

  async function removeLastImport(): Promise<void> {
    if (!lastImport) return;
    try {
      await api.deleteStatementImport(companyId, bankAccountId, lastImport.importId);
      setLastImport(null);
      setStatementBalance('');
      load();
    } catch (caught) {
      handleError(caught);
    }
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready || parsedBalance === null) return;
    setSubmitting(true);
    setFailure(null);
    try {
      const done = await api.createReconciliation(companyId, bankAccountId, idempotencyKey, {
        statementDate,
        statementBalance: parsedBalance,
        journalLineIds: [...cleared],
        statementLineIds: [...accepted]
          .filter(([, journalLineId]) => cleared.has(journalLineId))
          .map(([statementLineId]) => statementLineId),
      });
      setCompleted(done);
      setStatementBalance('');
      setIdempotencyKey(crypto.randomUUID());
      load();
    } catch (caught) {
      handleError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  const lines = workspace?.lines ?? [];
  const ledgerById = new Map(lines.map((line) => [line.journalLineId, line]));
  const statementLines = matches?.lines ?? [];
  const usedJournalLines = new Set(accepted.values());
  const pendingSuggestions = statementLines
    .filter(
      (line) =>
        !accepted.has(line.id) &&
        line.suggestedJournalLineId !== null &&
        !usedJournalLines.has(line.suggestedJournalLineId),
    )
    .map((line) => [line.id, line.suggestedJournalLineId!] as const);
  const allChecked = lines.length > 0 && lines.every((line) => cleared.has(line.journalLineId));

  return (
    <>
      {showImport && bankAccountId && (
        <StatementImport
          companyId={companyId}
          bankAccountId={bankAccountId}
          onImported={imported}
          onCancel={() => setShowImport(false)}
          onSessionLost={onSessionLost}
        />
      )}
      <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
        <div className="panel-head">
          <h2>مطابقة كشف بنك</h2>
          {canReconcile && bankAccountId && !showImport && (
            <button type="button" className="ghost" onClick={() => setShowImport(true)}>
              استيراد كشف
            </button>
          )}
        </div>
        {lastImport && (
          <p className="sub">
            استُورد {lastImport.inserted} سطرًا
            {lastImport.duplicates > 0
              ? ' • ' + lastImport.duplicates + ' سطر استُورد من قبل فتُجاهل'
              : ''}
            .{' '}
            {lastImport.inserted > 0 && (
              <button type="button" className="link" onClick={() => void removeLastImport()}>
                تراجع عن هذا الاستيراد
              </button>
            )}
          </p>
        )}
        {banks?.length === 0 && (
          <p className="sub">لا حساب بنكى نشط — أضفه من «الخزائن والبنوك».</p>
        )}
        <div className="form-row">
          <label className="grow">
            الحساب البنكى
            <select
              value={bankAccountId}
              onChange={(event) => {
                setBankAccountId(event.target.value);
                touched();
              }}
            >
              {(banks ?? []).map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {bank.nameAr} — {bank.accountCode}
                </option>
              ))}
            </select>
            <Hint>البنك الذى تريد مطابقة كشفه مع دفاترك.</Hint>
          </label>
          <label>
            تاريخ الكشف
            <input
              type="date"
              value={statementDate}
              onChange={(event) => {
                setStatementDate(event.target.value);
                touched();
              }}
            />
            <Hint>آخر تاريخ مطبوع فى كشف الحساب الذى أرسله البنك.</Hint>
          </label>
          <label>
            رصيد الكشف الختامى ({currency})
            <input
              inputMode="decimal"
              className={statementBalance && parsedBalance === null ? 'invalid' : ''}
              value={statementBalance}
              onChange={(event) => {
                setStatementBalance(event.target.value);
                touched();
              }}
            />
            <Hint>
              الرصيد المكتوب فى آخر سطر من كشف البنك. البرنامج يقارنه برصيدك ويُظهر الفرق.
            </Hint>
          </label>
        </div>

        {workspace && (
          <p className="sub">
            {workspace.previousDate ? (
              <>
                آخر مطابقة {workspace.previousDate} برصيد{' '}
                <bdi dir="ltr">{formatMoney(workspace.previousBalance)}</bdi>
              </>
            ) : (
              'أول مطابقة لهذا الحساب — الرصيد السابق صفر'
            )}
            {' • '}رصيد الدفتر فى {workspace.statementDate}:{' '}
            <bdi dir="ltr">{formatMoney(workspace.bookBalance)}</bdi>
          </p>
        )}
        {dateTooEarly && <p className="error">{ISSUE_LABELS.RECONCILIATION_DATE_NOT_AFTER}</p>}

        {workspace && lines.length === 0 && (
          <p className="sub">لا سطور معلقة على الحساب حتى هذا التاريخ.</p>
        )}
        {lines.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="تعليم الكل"
                      checked={allChecked}
                      onChange={() => {
                        setCleared(
                          allChecked ? new Set() : new Set(lines.map((line) => line.journalLineId)),
                        );
                        touched();
                      }}
                    />
                  </th>
                  <th>التاريخ</th>
                  <th>القيد</th>
                  <th>البيان</th>
                  <th>إيداع</th>
                  <th>سحب</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr
                    key={line.journalLineId}
                    className="clickable"
                    onClick={() => toggle(line.journalLineId)}
                  >
                    <td>
                      <input
                        type="checkbox"
                        aria-label={'ظهر فى الكشف: ' + (line.journalNumber ?? '')}
                        checked={cleared.has(line.journalLineId)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggle(line.journalLineId)}
                      />
                    </td>
                    <td className="nowrap">{line.postingDate}</td>
                    <td className="mono nowrap">{line.journalNumber}</td>
                    <td>
                      {line.reference ? line.reference + ' — ' : ''}
                      {line.memo ?? ''}
                    </td>
                    <td className="num-cell">
                      {Money.of(line.debit).isZero() ? '' : formatMoney(line.debit)}
                    </td>
                    <td className="num-cell">
                      {Money.of(line.credit).isZero() ? '' : formatMoney(line.credit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {statementLines.length > 0 && (
          <>
            <div className="panel-head">
              <h3>سطور كشف البنك المستورد ({statementLines.length})</h3>
              {canReconcile && pendingSuggestions.length > 0 && (
                <button type="button" className="ghost" onClick={() => accept(pendingSuggestions)}>
                  قبول كل المقترحات ({pendingSuggestions.length})
                </button>
              )}
            </div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>التاريخ</th>
                    <th>بيان البنك</th>
                    <th>المبلغ</th>
                    <th>المقابل فى الدفتر</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {statementLines.map((line) => {
                    const acceptedId = accepted.get(line.id);
                    const candidate =
                      line.candidates.find((item) => item.journalLineId === acceptedId) ??
                      line.candidates.find(
                        (item) => item.journalLineId === line.suggestedJournalLineId,
                      ) ??
                      null;
                    const ledgerLine = candidate ? ledgerById.get(candidate.journalLineId) : null;
                    const free =
                      candidate !== null &&
                      !acceptedId &&
                      !usedJournalLines.has(candidate.journalLineId);
                    return (
                      <tr key={line.id} className={acceptedId ? 'accepted' : ''}>
                        <td className="nowrap">{line.date}</td>
                        <td>
                          {line.description}
                          {line.reference ? <span className="mono"> {line.reference}</span> : null}
                        </td>
                        <td className="num-cell">{formatMoney(line.amount)}</td>
                        <td>
                          {ledgerLine && candidate ? (
                            <>
                              <span className="mono">{ledgerLine.journalNumber}</span>{' '}
                              {ledgerLine.postingDate}
                              <span className="label">
                                {' — '}
                                {candidate.reasons
                                  .filter((reason) => reason !== 'AMOUNT')
                                  .map((reason) => REASON_LABELS[reason] ?? reason)
                                  .join('، ')}
                              </span>
                            </>
                          ) : (
                            <span className="label">
                              لا مقابل بنفس المبلغ — سجّل المستند أولاً (مثلاً مصروف نقدى للرسوم)
                            </span>
                          )}
                        </td>
                        <td className="nowrap">
                          {canReconcile && acceptedId && (
                            <button
                              type="button"
                              className="link"
                              onClick={() => unaccept(line.id)}
                            >
                              إلغاء
                            </button>
                          )}
                          {canReconcile && free && candidate && (
                            <button
                              type="button"
                              className="link"
                              onClick={() => accept([[line.id, candidate.journalLineId]])}
                            >
                              قبول
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {workspace && (
          <div className="table-scroll">
            <table>
              <tbody>
                <tr>
                  <td>الرصيد السابق</td>
                  <td className="num-cell">{formatMoney(workspace.previousBalance)}</td>
                </tr>
                <tr>
                  <td>+ إيداعات ظهرت فى الكشف</td>
                  <td className="num-cell">
                    {formatMoney(summary?.clearedDeposits ?? Money.zero())}
                  </td>
                </tr>
                <tr>
                  <td>− مسحوبات ظهرت فى الكشف</td>
                  <td className="num-cell">
                    {formatMoney(summary?.clearedWithdrawals ?? Money.zero())}
                  </td>
                </tr>
                <tr className="closing">
                  <td>= الرصيد المُطابق</td>
                  <td className="num-cell total">
                    {summary ? formatMoney(summary.clearedBalance) : '—'}
                  </td>
                </tr>
                <tr>
                  <td>رصيد الكشف</td>
                  <td className="num-cell">
                    {parsedBalance !== null ? formatMoney(parsedBalance) : '—'}
                  </td>
                </tr>
                <tr>
                  <td>الفرق</td>
                  <td className="num-cell">
                    {summary && parsedBalance !== null ? (
                      <span className={'pill' + (summary.difference.isZero() ? '' : ' bad')}>
                        {summary.difference.isZero() ? 'متطابق' : formatMoney(summary.difference)}
                      </span>
                    ) : (
                      'أدخل رصيد الكشف'
                    )}
                  </td>
                </tr>
                <tr>
                  <td>معلق: إيداعات لم تظهر / مسحوبات لم تُصرف</td>
                  <td className="num-cell">
                    {formatMoney(outstanding.deposits)} / {formatMoney(outstanding.withdrawals)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {failure && <p className="error">{failure}</p>}
        {completed && (
          <p className="sub">
            اكتملت مطابقة {completed.statementDate} بـ {completed.clearedCount} سطرًا برصيد{' '}
            <bdi dir="ltr">{formatMoney(completed.statementBalance)}</bdi> {currency}.
          </p>
        )}
        {canReconcile ? (
          <div className="form-actions">
            <button type="submit" className="primary" disabled={!ready}>
              {submitting ? 'جارى الإتمام…' : 'إتمام المطابقة'}
            </button>
            <span className="label">المطابقة المكتملة نهائية — التصحيح فى الكشف التالى.</span>
          </div>
        ) : (
          <p className="sub">إتمام المطابقة للمحاسب والمالك.</p>
        )}
      </form>

      <div className="panel">
        <h2>المطابقات السابقة</h2>
        {history.length === 0 && <p className="sub">لا مطابقات مكتملة لهذا الحساب.</p>}
        {history.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>تاريخ الكشف</th>
                  <th>الرصيد السابق</th>
                  <th>إيداعات</th>
                  <th>مسحوبات</th>
                  <th>رصيد الكشف</th>
                  <th>رصيد الدفتر</th>
                  <th>السطور</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.id}>
                    <td className="nowrap">{item.statementDate}</td>
                    <td className="num-cell">{formatMoney(item.previousBalance)}</td>
                    <td className="num-cell">{formatMoney(item.clearedDeposits)}</td>
                    <td className="num-cell">{formatMoney(item.clearedWithdrawals)}</td>
                    <td className="num-cell">{formatMoney(item.statementBalance)}</td>
                    <td className="num-cell">{formatMoney(item.bookBalance)}</td>
                    <td>{item.clearedCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
